import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Check, Loader2, Minus, Plus, Users } from "lucide-react";
import { Reveal } from "@/components/latir/Motion";
import { EditorialText } from "@/components/latir/EditorialText";
import { useSEO } from "@/hooks/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MONTHS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function fmtDateLong(iso) {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  return `${DAYS[dt.getDay()]} ${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}`;
}
function fmtDateShort(iso) {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  const dow = DAYS[dt.getDay()].slice(0, 3);
  return `${dow.charAt(0).toUpperCase()}${dow.slice(1)} ${dt.getDate()} ${MONTHS[dt.getMonth()].slice(0, 3)}`;
}
function fmtTime(hhmm) {
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const ampm = h < 12 ? "a. m." : "p. m.";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}
/* Bloques de visita de 1h30: muestra el rango completo "10:30 a. m. – 12:00 p. m." */
function fmtRange(hhmm) {
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  const endTotal = h * 60 + m + 90;
  const eh = Math.floor(endTotal / 60) % 24;
  const em = endTotal % 60;
  const fmt = (hh, mm) => {
    const ampm = hh < 12 ? "a. m." : "p. m.";
    const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
    return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
  };
  return `${fmt(h, m)} – ${fmt(eh, em)}`;
}

/* Detección de iPhone/iPad: en iOS el botón principal es Apple Calendar (.ics
   abre directo la vista previa del evento); Google Calendar queda como opción
   secundaria abriendo la WEB (sin depender de la app de Google). */
const IS_IOS =
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

export default function Reservas() {
  useSEO({
    title: "Visitas guiadas — LATIR | Arteconciencia",
    description:
      "Reserva tu visita guiada a LATIR — El placer de vivir, la exposición de Arteconciencia en el Palacio Nacional de El Salvador.",
    canonical: "/reservas",
  });
  const [slots, setSlots] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1 date · 2 party · 3 form · 4 confirmation
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [partySize, setPartySize] = useState(2);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/visits/slots`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setSlots)
      .catch(() => setError("No pudimos cargar las visitas. Intenta más tarde."));
  }, []);

  const isPending = false; // capacidad dura: toda solicitud pública enviada queda confirmada
  const partyLabel = "Reserva confirmada al enviar";

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const goNext = () => {
    setStep((s) => Math.min(s + 1, 4));
    setTimeout(scrollTop, 40);
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 1));
    setTimeout(scrollTop, 40);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/visits/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slot_id: selectedSlot.id,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          party_size: partySize,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "No se pudo procesar la reserva.");
      setConfirmation(data);
      setStep(4);
      setTimeout(scrollTop, 40);
    } catch (e) {
      setError(e.message || "Error inesperado.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="paper-grain min-h-screen bg-paper pt-24 pb-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <Reveal>
          <p className="eyebrow text-latir-red">Visitas guiadas</p>
          <h1
            className="mt-3 font-display text-[2.2rem] sm:text-[3rem] leading-[1.02] text-ink"
            style={{ fontFamily: '"Instrument Serif", serif' }}
            data-testid="reservas-title"
          >
            Reserva tu visita a LATIR
          </h1>
          <EditorialText className="mt-4 text-[1.05rem] leading-[1.55] text-ink-soft max-w-2xl">
            Recorre la exposición acompañada por nuestro equipo curatorial. Selecciona
            una fecha disponible y reserva tu espacio. Cupo limitado.
          </EditorialText>
        </Reveal>

        <Stepper step={step} />

        {error && (
          <div
            className="mt-8 border-l-2 border-latir-red bg-paper-raised px-5 py-4 font-ui text-[0.85rem] text-ink-soft"
            data-testid="reservas-error"
          >
            {error}
          </div>
        )}

        <div className="mt-10">
          {step === 1 && (
            <StepDate
              slots={slots}
              selectedSlot={selectedSlot}
              onSelect={(s) => {
                setSelectedSlot(s);
                setError(null);
                goNext();
              }}
            />
          )}
          {step === 2 && selectedSlot && (
            <StepParty
              slot={selectedSlot}
              partySize={partySize}
              setPartySize={setPartySize}
              onBack={goBack}
              onNext={goNext}
            />
          )}
          {step === 3 && selectedSlot && (
            <StepForm
              slot={selectedSlot}
              partySize={partySize}
              partyLabel={partyLabel}
              form={form}
              setForm={setForm}
              onBack={goBack}
              onSubmit={submit}
              submitting={submitting}
              isPending={isPending}
            />
          )}
          {step === 4 && confirmation && (
            <StepDone confirmation={confirmation} navigate={navigate} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ————————————————————————————————————————————————— Stepper ——— */
function Stepper({ step }) {
  const items = ["Fecha", "Personas", "Datos", "Confirmación"];
  return (
    <div className="mt-10 flex items-center gap-4 flex-wrap" data-testid="reservas-stepper">
      {items.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={label} className="flex items-center gap-3">
            <span
              className={`inline-flex w-6 h-6 items-center justify-center rounded-full border font-ui text-[0.68rem] tracking-[0.05em] ${
                active
                  ? "bg-latir-red border-latir-red text-paper-raised"
                  : done
                  ? "bg-ink border-ink text-paper-raised"
                  : "border-line text-ink-mute"
              }`}
            >
              {done ? <Check className="w-3 h-3" strokeWidth={2.2} /> : n}
            </span>
            <span
              className={`font-ui uppercase tracking-[0.22em] text-[0.7rem] ${
                active ? "text-ink" : done ? "text-ink-soft" : "text-ink-mute"
              }`}
            >
              {label}
            </span>
            {i < items.length - 1 && <span className="hidden sm:inline-block w-8 h-px bg-line" />}
          </div>
        );
      })}
    </div>
  );
}

/* ————————————————————————————————————————————— Step 1 · date ——— */
function StepDate({ slots, selectedSlot, onSelect }) {
  if (slots === null) {
    return (
      <div className="flex items-center gap-3 py-8 text-ink-mute font-ui text-[0.9rem]">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando fechas disponibles…
      </div>
    );
  }
  if (slots.length === 0) {
    return (
      <div className="py-8 text-ink-soft font-editorial italic text-[1.05rem]">
        En este momento no hay fechas disponibles. Nuevas fechas se habilitarán próximamente.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="reservas-step-date">
      <p className="eyebrow text-ink-mute">Selecciona una fecha</p>
      <ul className="divide-y divide-line border-t border-b border-line">
        {slots.map((s) => {
          const selected = selectedSlot && selectedSlot.id === s.id;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSelect(s)}
                data-testid={`slot-option-${s.id}`}
                className={`w-full flex items-center justify-between text-left py-5 gap-6 transition-colors hover:bg-paper-raised cursor-pointer ${selected ? "bg-paper-raised" : ""}`}
              >
                <div className="flex items-center gap-4 sm:gap-6 flex-1 min-w-0">
                  <CalendarDays className="w-4 h-4 text-latir-red shrink-0" strokeWidth={1.4} />
                  <div className="min-w-0">
                    <p
                      className="font-display text-[1.2rem] sm:text-[1.4rem] leading-tight text-ink capitalize"
                      style={{ fontFamily: '"Instrument Serif", serif' }}
                    >
                      {fmtDateLong(s.date)}
                    </p>
                    <p className="mt-1 font-ui text-[0.78rem] uppercase tracking-[0.18em] text-ink-soft">
                      {fmtRange(s.time)}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0 max-w-[45%]">
                  {s.full ? (
                    <span
                      className="font-ui uppercase tracking-[0.2em] text-[0.68rem] text-latir-red"
                      data-testid={`slot-status-completo-${s.id}`}
                    >
                      Disponibilidad sujeta a confirmación
                    </span>
                  ) : s.remaining <= 10 ? (
                    <span
                      className="font-ui uppercase tracking-[0.2em] text-[0.68rem] text-latir-red"
                      data-testid={`slot-status-pocos-${s.id}`}
                    >
                      Pocos espacios · quedan {s.remaining}
                    </span>
                  ) : (
                    <span
                      className="font-ui text-[0.78rem] uppercase tracking-[0.18em] text-ink-mute"
                      data-testid={`slot-status-disponible-${s.id}`}
                    >
                      Disponible
                    </span>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ————————————————————————————————————————————— Step 2 · party ——— */
function StepParty({ slot, partySize, setPartySize, onBack, onNext }) {
  const max = 200; // límite de cordura del input; el cupo real sí bloquea
  const min = 1;
  const exceeds = partySize > slot.remaining;

  return (
    <div data-testid="reservas-step-party" className="space-y-8">
      <SlotSummary slot={slot} />

      <div>
        <p className="eyebrow text-ink-mute">Número de personas</p>
        <div className="mt-6 flex items-center gap-6">
          <button
            type="button"
            onClick={() => setPartySize((v) => Math.max(min, v - 1))}
            data-testid="party-minus"
            className="w-11 h-11 border border-line flex items-center justify-center hover:border-latir-red hover:text-latir-red transition-colors"
            aria-label="Menos"
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="number"
            min={min}
            max={max}
            value={partySize}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!Number.isNaN(v)) setPartySize(Math.min(max, Math.max(min, v)));
            }}
            className="w-28 text-center bg-transparent border-b border-line focus:border-latir-red outline-none font-display text-[3rem] leading-none text-ink py-1"
            style={{ fontFamily: '"Instrument Serif", serif' }}
            data-testid="party-value"
            aria-label="Número de personas"
          />
          <span className="font-ui uppercase tracking-[0.2em] text-[0.7rem] text-ink-mute">
            persona{partySize === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={() => setPartySize((v) => Math.min(max, v + 1))}
            data-testid="party-plus"
            className="w-11 h-11 border border-line flex items-center justify-center hover:border-latir-red hover:text-latir-red transition-colors"
            aria-label="Más"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {exceeds && (
          <p className="mt-3 font-ui text-[0.78rem] text-latir-red" data-testid="party-overcapacity-note">
            El cupo disponible en esta fecha es de {slot.remaining} personas.
          </p>
        )}
      </div>

      <NavButtons onBack={onBack} onNext={onNext} nextDisabled={exceeds} />
    </div>
  );
}

/* ————————————————————————————————————————————— Step 3 · form ——— */
function StepForm({
  slot,
  partySize,
  partyLabel,
  form,
  setForm,
  onBack,
  onSubmit,
  submitting,
  isPending,
}) {
  const valid = useMemo(() => {
    const ok = form.name.trim().length >= 2 && /@/.test(form.email) && form.phone.trim().length >= 5;
    return ok;
  }, [form]);

  return (
    <div data-testid="reservas-step-form" className="space-y-8">
      <SlotSummary slot={slot} extraLine={`${partySize} persona${partySize === 1 ? "" : "s"} · ${partyLabel}`} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field label="Nombre completo" testid="field-name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-transparent border-b border-line focus:border-latir-red outline-none font-editorial text-[1.1rem] text-ink py-2 transition-colors"
            data-testid="input-name"
          />
        </Field>
        <Field label="Correo electrónico" testid="field-email">
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-transparent border-b border-line focus:border-latir-red outline-none font-editorial text-[1.1rem] text-ink py-2 transition-colors"
            data-testid="input-email"
          />
        </Field>
        <Field label="Teléfono / WhatsApp" testid="field-phone">
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-transparent border-b border-line focus:border-latir-red outline-none font-editorial text-[1.1rem] text-ink py-2 transition-colors"
            data-testid="input-phone"
          />
        </Field>
      </div>

      <NavButtons
        onBack={onBack}
        onNext={onSubmit}
        nextLabel={isPending ? "Enviar solicitud" : "Confirmar reserva"}
        nextDisabled={!valid || submitting}
        submitting={submitting}
      />
    </div>
  );
}

/* ————————————————————————————————————————————— Step 4 · done ——— */
function StepDone({ confirmation, navigate }) {
  const b = confirmation.booking;
  const pending = confirmation.status === "pending";
  return (
    <div
      className="border border-line bg-paper-raised px-8 sm:px-12 py-12 text-center"
      data-testid="reservas-confirmation"
    >
      <div className="w-12 h-12 mx-auto flex items-center justify-center border border-latir-red text-latir-red">
        <Check className="w-5 h-5" strokeWidth={1.6} />
      </div>
      <h2
        className="mt-8 font-display text-[2rem] sm:text-[2.4rem] leading-[1.05] text-ink"
        style={{ fontFamily: '"Instrument Serif", serif' }}
      >
        {pending ? "Solicitud recibida." : "Tu visita está confirmada."}
      </h2>
      <p className="mt-3 font-editorial italic text-[1.1rem] text-ink-soft">
        LATIR — Arteconciencia · Palacio Nacional
      </p>

      <div className="mt-10 max-w-md mx-auto text-left border-t border-line divide-y divide-line">
        <SummaryRow label="Fecha" value={fmtDateLong(b.date)} />
        <SummaryRow label="Hora" value={fmtRange(b.time)} />
        <SummaryRow label="Personas" value={b.party_size} />
        <SummaryRow label="A nombre de" value={b.name} />
      </div>

      {!pending && confirmation.calendar && (
        <>
          <div className="mt-8 space-y-2" data-testid="confirmation-checklist">
            <p className="font-ui text-[0.78rem] uppercase tracking-[0.18em] text-ink flex items-center justify-center gap-2">
              <Check className="w-3.5 h-3.5 text-latir-red" strokeWidth={2} />
              Reserva confirmada
            </p>
            <p className="font-ui text-[0.78rem] uppercase tracking-[0.18em] text-ink flex items-center justify-center gap-2">
              <Check className="w-3.5 h-3.5 text-latir-red" strokeWidth={2} />
              Invitación de calendario disponible
            </p>
          </div>
          <p className="mt-6 font-editorial text-[1rem] leading-[1.6] text-ink-soft max-w-md mx-auto">
            Tu visita guiada a LATIR ha sido reservada. Te enviamos la confirmación y una
            invitación de calendario a tu correo.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            {IS_IOS ? (
              <>
                <a
                  href={confirmation.calendar.ics_url}
                  data-testid="confirmation-add-ics"
                  className="px-6 py-3 bg-ink text-paper-raised hover:bg-latir-red font-ui uppercase tracking-[0.2em] text-[0.72rem] transition-colors"
                >
                  Añadir a Apple Calendar
                </a>
                <a
                  href={confirmation.calendar.google_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="confirmation-add-gcal"
                  className="px-6 py-3 border border-line hover:border-ink font-ui uppercase tracking-[0.2em] text-[0.72rem] text-ink-soft hover:text-ink transition-colors"
                >
                  Abrir en Google Calendar
                </a>
              </>
            ) : (
              <>
                <a
                  href={confirmation.calendar.google_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="confirmation-add-gcal"
                  className="px-6 py-3 bg-ink text-paper-raised hover:bg-latir-red font-ui uppercase tracking-[0.2em] text-[0.72rem] transition-colors"
                >
                  Añadir a Google Calendar
                </a>
                <a
                  href={confirmation.calendar.ics_url}
                  data-testid="confirmation-add-ics"
                  className="px-6 py-3 border border-line hover:border-ink font-ui uppercase tracking-[0.2em] text-[0.72rem] text-ink-soft hover:text-ink transition-colors"
                >
                  Añadir a Apple / Outlook
                </a>
              </>
            )}
          </div>
        </>
      )}

      {pending && (
        <p className="mt-8 font-editorial italic text-[0.98rem] text-ink-soft max-w-md mx-auto">
          Tu solicitud fue recibida y está pendiente de confirmación. El equipo de
          Arteconciencia te contactará pronto.
        </p>
      )}

      <p className="mt-8 font-ui text-[0.78rem] uppercase tracking-[0.18em] text-ink-mute">
        Enviamos una copia a {b.email}
      </p>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          onClick={() => navigate("/")}
          className="px-6 py-3 border border-line hover:border-ink font-ui uppercase tracking-[0.2em] text-[0.72rem] text-ink-soft hover:text-ink transition-colors"
          data-testid="confirmation-home"
        >
          Volver al inicio
        </button>
        <button
          onClick={() => navigate("/agenda")}
          className="px-6 py-3 bg-ink text-paper-raised hover:bg-latir-red font-ui uppercase tracking-[0.2em] text-[0.72rem] transition-colors"
          data-testid="confirmation-programa"
        >
          Ver el programa completo
        </button>
      </div>
    </div>
  );
}

/* ————————————————————————————————————————————————— Helpers ——— */
function SlotSummary({ slot, extraLine }) {
  return (
    <div className="border-l-2 border-latir-red pl-4">
      <p className="eyebrow text-ink-mute">Tu visita</p>
      <p
        className="mt-1 font-display text-[1.35rem] leading-tight text-ink capitalize"
        style={{ fontFamily: '"Instrument Serif", serif' }}
      >
        {fmtDateLong(slot.date)} · {fmtRange(slot.time)}
      </p>
      {extraLine && (
        <p className="mt-1 font-ui text-[0.78rem] uppercase tracking-[0.18em] text-ink-soft">
          {extraLine}
        </p>
      )}
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="eyebrow text-ink-mute">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3 gap-6">
      <span className="font-ui uppercase tracking-[0.18em] text-[0.68rem] text-ink-mute">{label}</span>
      <span className="font-editorial text-[1rem] text-ink text-right capitalize">{value}</span>
    </div>
  );
}

function NavButtons({ onBack, onNext, nextLabel = "Continuar", nextDisabled, submitting }) {
  return (
    <div className="flex flex-col sm:flex-row-reverse items-center gap-4 pt-6 border-t border-line">
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        data-testid="reservas-next"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-ink text-paper-raised px-8 py-3 font-ui uppercase tracking-[0.22em] text-[0.72rem] hover:bg-latir-red transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {nextLabel}
      </button>
      <button
        type="button"
        onClick={onBack}
        data-testid="reservas-back"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3 border border-line hover:border-ink font-ui uppercase tracking-[0.22em] text-[0.72rem] text-ink-soft hover:text-ink transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Atrás
      </button>
    </div>
  );
}
